import { getTecgymAdminUsersURL, updateTecgymAdminUserURL } from "./apiUrl";
import { getAdminUser, getLoggedInAdminUserId, isLoggedInAdmin, matchesLoggedInAdminUser } from "./auth";
import { _get, _put } from "./fetch";

export const USER_ATTENDANCE_FIELD = "Attendance";
export const USER_LEAVES_FIELD = "Leaves";
export const USER_WORK_FROM_HOME_FIELD = "WorkFromHome";

export const USER_ATTENDANCE_KEYS = [USER_ATTENDANCE_FIELD, "attendance", "attendances"];
export const USER_LEAVES_KEYS = [USER_LEAVES_FIELD, "leaves", "employeeLeaves", "employeeLeave"];
export const USER_WORK_FROM_HOME_KEYS = [
  USER_WORK_FROM_HOME_FIELD,
  "workFromHome",
  "Work from home",
  "employeeWorkFromHome",
  "employeeWorkFromHomes"
];

export function extractAdminUserList(data) {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeAdminUserRecord).filter(Boolean);
  }

  const keys = [
    "response",
    "data",
    "users",
    "tecgymAdminUsers",
    "adminUsers",
    "employees",
    "result",
    "results",
    "items"
  ];

  for (const key of keys) {
    const value = data[key];
    if (Array.isArray(value)) {
      return value.map(normalizeAdminUserRecord).filter(Boolean);
    }
    if (value && typeof value === "object") {
      const nestedList = extractAdminUserList(value);
      if (nestedList.length > 0) return nestedList;
    }
  }

  const normalized = normalizeAdminUserRecord(data);
  if (
    normalized?._id ||
    normalized?.id ||
    normalized?.email ||
    normalized?.loginId ||
    normalized?.name
  ) {
    return [normalized];
  }

  return [];
}

function normalizeAdminUserRecord(record) {
  if (!record || typeof record !== "object" || Array.isArray(record)) {
    return record;
  }

  const nested = record.user || record.tecgymAdminUser || record.adminUser;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested;
  }

  return record;
}

export function getAdminUserDisplayName(user) {
  return user?.name || user?.userName || user?.fullName || user?.employeeName || user?.firstName || "";
}

export function getVisibleAdminUsers(users, loggedInUser = getAdminUser()) {
  if (!Array.isArray(users) || users.length === 0) {
    return [];
  }

  if (isLoggedInAdmin(loggedInUser)) {
    return users;
  }

  return users.filter((user) => matchesLoggedInAdminUser(user, loggedInUser));
}

export function getAdminUserDisplayNames(users) {
  const names = new Set();

  users.forEach((user) => {
    const name = String(getAdminUserDisplayName(user) || "").trim();
    if (name) {
      names.add(name);
    }
  });

  return [...names].sort((first, second) => first.localeCompare(second));
}

export function readUserListField(user, keys) {
  const names = Array.isArray(keys) ? keys : [keys];

  for (const key of names) {
    const value = user?.[key];
    if (Array.isArray(value) && value.length > 0) {
      return value;
    }
  }

  for (const key of names) {
    const value = user?.[key];
    if (Array.isArray(value)) {
      return value;
    }
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return [value];
    }
  }

  return [];
}

export function buildUserActivityLists(user, overrides = {}) {
  return {
    [USER_ATTENDANCE_FIELD]: Object.prototype.hasOwnProperty.call(overrides, USER_ATTENDANCE_FIELD)
      ? overrides[USER_ATTENDANCE_FIELD]
      : readUserListField(user, USER_ATTENDANCE_KEYS),
    [USER_LEAVES_FIELD]: Object.prototype.hasOwnProperty.call(overrides, USER_LEAVES_FIELD)
      ? overrides[USER_LEAVES_FIELD]
      : readUserListField(user, USER_LEAVES_KEYS),
    [USER_WORK_FROM_HOME_FIELD]: Object.prototype.hasOwnProperty.call(overrides, USER_WORK_FROM_HOME_FIELD)
      ? overrides[USER_WORK_FROM_HOME_FIELD]
      : readUserListField(user, USER_WORK_FROM_HOME_KEYS)
  };
}

export async function updateTecgymAdminUserFields(userId, fields) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new Error("Unable to update this user because the user id is missing.");
  }

  const response = await _put(updateTecgymAdminUserURL(id), fields);
  const httpStatus = typeof response?.status === "number" ? response.status : 0;
  if (!response || response.success === false || response.status === false || httpStatus >= 400) {
    throw new Error(response?.message || response?.error || "Unable to update the user.");
  }

  return response;
}

export async function saveLoggedInAdminProfileImage(image) {
  const userId = getLoggedInAdminUserId();
  if (!userId || userId === "dev-admin-tecfolk") {
    return null;
  }

  try {
    return await updateTecgymAdminUserFields(userId, {
      profileImage: image || ""
    });
  } catch (error) {
    console.warn("Profile image stayed on this browser. The server did not store it.", error);
    return null;
  }
}

export async function fetchTecgymAdminUsers() {
  const data = await _get(getTecgymAdminUsersURL(), { useCache: false });
  if (!data || data.success === false) {
    throw new Error(data?.message || data?.error || "Failed to load employees.");
  }

  return extractAdminUserList(data);
}
